import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { motion } from 'framer-motion';
import { pushNotificationService } from '../services/pushNotificationService';
import { useAuth } from '../contexts/AuthContext';

const PushNotificationPrompt = ({ onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEnableNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permission = await pushNotificationService?.requestPermission();
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        await pushNotificationService?.subscribeToPush(user?.id);
        
        // Show success and close
        setTimeout(() => {
          onClose?.(true);
        }, 1000);
      } else {
        setError('Permission denied. You can enable notifications later in your browser settings.');
      }
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError('Failed to enable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    onClose?.(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed bottom-6 right-6 z-50 max-w-md"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
            <Icon name="Bell" className="w-6 h-6 text-accent" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Enable Push Notifications
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Get instant alerts when duels start, opponents submit evidence, and winners are declared!
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleEnableNotifications}
                disabled={loading}
                className="flex-1 bg-accent hover:bg-accent/90 text-white"
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Icon name="Bell" className="w-4 h-4 mr-2" />
                    Enable
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleDismiss}
                variant="outline"
                disabled={loading}
                className="flex-1"
              >
                Later
              </Button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={loading}
          >
            <Icon name="X" className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PushNotificationPrompt;